/**
 * ThemeManager -- Framework-agnostic singleton for theme management.
 *
 * Responsibilities:
 * - Persist mode + brand to localStorage
 * - Set data-theme, color-scheme, data-brand on <html>
 * - Set .dark/.light class for backwards compatibility (removed Phase 9)
 * - Listen for OS prefers-color-scheme changes when mode is "system"
 * - Pub/sub for framework reactivity via subscribe()
 * - Migrate from legacy storage keys
 */

import type {
    BrandPalette,
    EffectiveTheme,
    ThemeListener,
    ThemeMode,
    ThemeSnapshot,
    ThemeState,
} from './types';

const STORAGE_KEY = 'fnl-theme';
const LEGACY_KEYS = ['appearance', 'fnl-ui'];

let instance: ThemeManager | null = null;

export class ThemeManager {
    private state: ThemeState;
    private mediaQuery: MediaQueryList;
    private listeners: Set<ThemeListener> = new Set();
    private mediaHandler: () => void;

    private constructor() {
        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.state = this.loadState();
        this.mediaHandler = () => {
            if (this.state.mode === 'system') {
                this.apply();
                this.notifyListeners();
            }
        };
        this.mediaQuery.addEventListener('change', this.mediaHandler);
        this.apply();
    }

    /** Get or create the singleton instance. */
    static getInstance(): ThemeManager {
        if (!instance) {
            instance = new ThemeManager();
        }
        return instance;
    }

    /** Current snapshot for reading state. */
    getSnapshot(): ThemeSnapshot {
        return {
            mode: this.state.mode,
            effectiveTheme: this.resolveTheme(),
            brand: this.state.brand,
        };
    }

    /** Change the theme mode (light / dark / system). */
    setMode(mode: ThemeMode): void {
        this.state.mode = mode;
        this.persist();
        this.apply();
        this.notifyListeners();
    }

    /** Change the brand palette. */
    setBrand(brand: BrandPalette): void {
        this.state.brand = brand;
        this.persist();
        this.apply();
        this.notifyListeners();
    }

    /** Subscribe to theme changes. Returns unsubscribe function. */
    subscribe(listener: ThemeListener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /** Clean up legacy localStorage keys from previous theme implementations. */
    cleanupLegacyStorage(): void {
        for (const key of LEGACY_KEYS) {
            localStorage.removeItem(key);
        }
        // Remove legacy cookie
        document.cookie = 'appearance=; path=/; max-age=0';
    }

    /** Destroy the singleton (for testing or cleanup). */
    destroy(): void {
        this.mediaQuery.removeEventListener('change', this.mediaHandler);
        this.listeners.clear();
        instance = null;
    }

    // ---- Private ----

    private resolveTheme(): EffectiveTheme {
        if (this.state.mode === 'system') {
            return this.mediaQuery.matches ? 'dark' : 'light';
        }
        return this.state.mode;
    }

    private apply(): void {
        const root = document.documentElement;
        const effective = this.resolveTheme();

        // data-theme drives primitive token swaps in CSS
        root.setAttribute('data-theme', effective);

        // color-scheme tells the browser (scrollbars, form controls, light-dark())
        root.style.setProperty('color-scheme', effective);

        // data-brand drives brand palette CSS
        root.setAttribute('data-brand', this.state.brand);
    }

    private persist(): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
        } catch {
            // Silently fail if localStorage is full or unavailable
        }
    }

    private notifyListeners(): void {
        const snapshot = this.getSnapshot();
        for (const listener of this.listeners) {
            listener(snapshot);
        }
    }

    private loadState(): ThemeState {
        const defaults: ThemeState = { mode: 'system', brand: 'default' };

        try {
            // Try new key first
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as Partial<ThemeState>;
                return {
                    mode: this.isValidMode(parsed.mode)
                        ? parsed.mode
                        : defaults.mode,
                    brand: this.isValidBrand(parsed.brand)
                        ? parsed.brand
                        : defaults.brand,
                };
            }

            // Migrate from legacy Pinia persist key
            const legacyPinia = localStorage.getItem('fnl-ui');
            if (legacyPinia) {
                const parsed = JSON.parse(legacyPinia) as Record<
                    string,
                    unknown
                >;
                const legacyTheme = parsed.theme;
                if (this.isValidMode(legacyTheme as string)) {
                    const state: ThemeState = {
                        mode: legacyTheme as ThemeMode,
                        brand: defaults.brand,
                    };
                    this.state = state;
                    this.persist();
                    return state;
                }
            }

            // Migrate from legacy appearance key
            const legacyAppearance = localStorage.getItem('appearance');
            if (legacyAppearance && this.isValidMode(legacyAppearance)) {
                const state: ThemeState = {
                    mode: legacyAppearance as ThemeMode,
                    brand: defaults.brand,
                };
                this.state = state;
                this.persist();
                return state;
            }
        } catch {
            // Corrupted storage, use defaults
        }

        return defaults;
    }

    private isValidMode(value: unknown): value is ThemeMode {
        return value === 'light' || value === 'dark' || value === 'system';
    }

    private isValidBrand(value: unknown): value is BrandPalette {
        return (
            value === 'default' ||
            value === 'ocean' ||
            value === 'forest' ||
            value === 'sunset' ||
            value === 'ember' ||
            value === 'gold'
        );
    }
}

/** Convenience function to get the ThemeManager singleton. */
export function getThemeManager(): ThemeManager {
    return ThemeManager.getInstance();
}
